
import React, { useState } from 'react';
import { 
  Calculator, 
  FileText, 
  Download, 
  Upload, 
  CheckCircle, 
  XCircle, 
  Clock, 
  AlertCircle,
  Plus, 
  Search, 
  Filter,
  Eye,
  Edit,
  Trash2,
  Calendar,
  DollarSign,
  Users,
  Package,
  RefreshCw,
  FileSpreadsheet,
  Receipt
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';

const Reconciliation = () => {
  const { toast } = useToast();

  // Mock data for reconciliation records
  const [reconciliationRecords] = useState([
    {
      id: 'REC001',
      weekPeriod: '02/01/2025 - 08/01/2025',
      customerId: 'KH001',
      customerName: 'Nguyễn Văn A',
      totalOrders: 25,
      totalCOD: 15500000,
      serviceFee: 875000,
      netAmount: 14625000,
      status: 'paid',
      createdAt: '2025-01-09T09:00:00+07:00',
      paidAt: '2025-01-10T14:30:00+07:00',
      receiptUrl: '/receipts/REC001.pdf',
      orders: [
        { id: 'DH001', cod: 500000, fee: 25000 },
        { id: 'DH002', cod: 750000, fee: 37500 },
        { id: 'DH003', cod: 300000, fee: 15000 }
      ]
    },
    {
      id: 'REC002',
      weekPeriod: '26/12/2024 - 01/01/2025',
      customerId: 'KH002',
      customerName: 'Trần Thị B',
      totalOrders: 18,
      totalCOD: 8900000,
      serviceFee: 445000,
      netAmount: 8455000,
      status: 'pending',
      createdAt: '2025-01-02T09:00:00+07:00',
      orders: [
        { id: 'DH004', cod: 400000, fee: 20000 },
        { id: 'DH005', cod: 600000, fee: 30000 }
      ]
    },
    {
      id: 'REC003',
      weekPeriod: '19/12/2024 - 25/12/2024',
      customerId: 'KH003',
      customerName: 'Lê Minh C',
      totalOrders: 32,
      totalCOD: 22100000,
      serviceFee: 1105000,
      netAmount: 20995000,
      status: 'disputed',
      createdAt: '2024-12-26T09:00:00+07:00',
      disputeReason: 'Thiếu 3 đơn hàng trong báo cáo',
      orders: [
        { id: 'DH006', cod: 800000, fee: 40000 },
        { id: 'DH007', cod: 1200000, fee: 60000 }
      ]
    }
  ]);

  // Mock data for COD collection
  const [codRecords] = useState([
    {
      id: 'COD001',
      orderId: 'DH015',
      customerName: 'Phạm Văn D',
      amount: 850000,
      collectedAt: '2025-01-13T10:30:00+07:00',
      driver: 'Nguyễn Tài Xế',
      status: 'collected',
      reconciled: false
    },
    {
      id: 'COD002',
      orderId: 'DH016',
      customerName: 'Ngô Thị E',
      amount: 1200000,
      collectedAt: '2025-01-13T14:15:00+07:00',
      driver: 'Trần Tài Xế',
      status: 'collected',
      reconciled: true
    }
  ]);

  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedRecords, setSelectedRecords] = useState([]);

  const getStatusColor = (status) => {
    switch (status) {
      case 'paid':
        return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
      case 'disputed':
        return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
      case 'processing':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'paid':
        return <CheckCircle className="h-4 w-4" />;
      case 'pending':
        return <Clock className="h-4 w-4" />;
      case 'disputed':
        return <AlertCircle className="h-4 w-4" />;
      case 'processing':
        return <RefreshCw className="h-4 w-4" />;
      default:
        return <XCircle className="h-4 w-4" />;
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'paid':
        return 'Đã thanh toán';
      case 'pending':
        return 'Chờ thanh toán';
      case 'disputed':
        return 'Có tranh chấp';
      case 'processing':
        return 'Đang xử lý';
      default:
        return 'Không xác định';
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('vi-VN', {
      style: 'currency',
      currency: 'VND'
    }).format(amount);
  };

  const handleMarkAsPaid = (recordId) => {
    toast({
      title: "Đánh dấu đã thanh toán",
      description: "Bản ghi đối soát đã được đánh dấu là đã thanh toán",
    });
  };

  const handleCreateDispute = (recordId) => {
    toast({
      title: "Tạo tranh chấp",
      description: "Yêu cầu tranh chấp đã được tạo và gửi đến bộ phận xử lý",
    });
  };

  const handleExportRecord = (recordId) => {
    toast({
      title: "Xuất báo cáo",
      description: "Đang xuất báo cáo đối soát...",
    });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Đối soát tài chính</h1>
          <p className="text-muted-foreground">Quản lý đối soát COD và thanh toán</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline">
            <Upload className="h-4 w-4 mr-2" />
            Import dữ liệu
          </Button>
          <Button variant="outline">
            <Download className="h-4 w-4 mr-2" />
            Xuất báo cáo
          </Button>
          <Dialog>
            <DialogTrigger asChild>
              <Button>
                <Plus className="h-4 w-4 mr-2" />
                Tạo đối soát mới
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Tạo đối soát mới</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="customer">Khách hàng</Label>
                  <Select>
                    <SelectTrigger>
                      <SelectValue placeholder="Chọn khách hàng" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="KH001">Nguyễn Văn A</SelectItem>
                      <SelectItem value="KH002">Trần Thị B</SelectItem>
                      <SelectItem value="KH003">Lê Minh C</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="fromDate">Từ ngày</Label>
                    <Input type="date" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="toDate">Đến ngày</Label>
                    <Input type="date" />
                  </div>
                </div>
                <Button className="w-full">Tạo đối soát</Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Tổng đối soát</p>
                <p className="text-2xl font-bold">{reconciliationRecords.length}</p>
              </div>
              <Calculator className="h-8 w-8 text-muted-foreground" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Chờ thanh toán</p>
                <p className="text-2xl font-bold text-yellow-600">
                  {reconciliationRecords.filter(r => r.status === 'pending').length}
                </p>
              </div>
              <Clock className="h-8 w-8 text-yellow-600" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Đã thanh toán</p>
                <p className="text-2xl font-bold text-green-600">
                  {reconciliationRecords.filter(r => r.status === 'paid').length}
                </p>
              </div>
              <CheckCircle className="h-8 w-8 text-green-600" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Tổng tiền</p>
                <p className="text-2xl font-bold">
                  {formatCurrency(reconciliationRecords.reduce((sum, r) => sum + r.netAmount, 0))}
                </p>
              </div>
              <DollarSign className="h-8 w-8 text-muted-foreground" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Content */}
      <Tabs defaultValue="reconciliation" className="space-y-4">
        <TabsList>
          <TabsTrigger value="reconciliation">Đối soát</TabsTrigger>
          <TabsTrigger value="cod">Thu COD</TabsTrigger>
          <TabsTrigger value="reports">Báo cáo</TabsTrigger>
        </TabsList>

        <TabsContent value="reconciliation" className="space-y-4">
          {/* Filters */}
          <Card>
            <CardContent className="p-4">
              <div className="flex flex-col sm:flex-row gap-4">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Tìm kiếm theo khách hàng, mã đối soát..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                  />
                </div>
                
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="w-full sm:w-48">
                    <Filter className="h-4 w-4 mr-2" />
                    <SelectValue placeholder="Lọc theo trạng thái" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tất cả trạng thái</SelectItem>
                    <SelectItem value="pending">Chờ thanh toán</SelectItem>
                    <SelectItem value="paid">Đã thanh toán</SelectItem>
                    <SelectItem value="disputed">Có tranh chấp</SelectItem>
                    <SelectItem value="processing">Đang xử lý</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          {/* Reconciliation Records Table */}
          <Card>
            <CardHeader>
              <CardTitle>Danh sách đối soát</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Mã đối soát</TableHead>
                      <TableHead>Khách hàng</TableHead>
                      <TableHead>Kỳ đối soát</TableHead>
                      <TableHead>Đơn hàng</TableHead>
                      <TableHead>COD</TableHead>
                      <TableHead>Phí dịch vụ</TableHead>
                      <TableHead>Thực nhận</TableHead>
                      <TableHead>Trạng thái</TableHead>
                      <TableHead>Thao tác</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {reconciliationRecords.map((record) => (
                      <TableRow key={record.id}>
                        <TableCell className="font-medium">{record.id}</TableCell>
                        <TableCell>
                          <div>
                            <div className="font-medium">{record.customerName}</div>
                            <div className="text-sm text-muted-foreground">{record.customerId}</div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            <Calendar className="h-3 w-3" />
                            <span className="text-sm">{record.weekPeriod}</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            <Package className="h-3 w-3" />
                            <span>{record.totalOrders} đơn</span>
                          </div>
                        </TableCell>
                        <TableCell className="font-medium text-blue-600">
                          {formatCurrency(record.totalCOD)}
                        </TableCell>
                        <TableCell className="text-red-600">
                          -{formatCurrency(record.serviceFee)}
                        </TableCell>
                        <TableCell className="font-medium text-green-600">
                          {formatCurrency(record.netAmount)}
                        </TableCell>
                        <TableCell>
                          <Badge className={getStatusColor(record.status)}>
                            <div className="flex items-center gap-1">
                              {getStatusIcon(record.status)}
                              {getStatusText(record.status)}
                            </div>
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            <Dialog>
                              <DialogTrigger asChild>
                                <Button size="sm" variant="ghost">
                                  <Eye className="h-4 w-4" />
                                </Button>
                              </DialogTrigger>
                              <DialogContent className="max-w-2xl">
                                <DialogHeader>
                                  <DialogTitle>Chi tiết đối soát {record.id}</DialogTitle>
                                </DialogHeader>
                                <div className="space-y-4">
                                  <div className="grid grid-cols-2 gap-4">
                                    <Card>
                                      <CardHeader className="pb-3">
                                        <CardTitle className="text-base">Thông tin khách hàng</CardTitle>
                                      </CardHeader>
                                      <CardContent className="text-sm space-y-2">
                                        <div><strong>Tên:</strong> {record.customerName}</div>
                                        <div><strong>Mã KH:</strong> {record.customerId}</div>
                                        <div><strong>Kỳ đối soát:</strong> {record.weekPeriod}</div>
                                      </CardContent>
                                    </Card>
                                    <Card>
                                      <CardHeader className="pb-3">
                                        <CardTitle className="text-base">Tóm tắt tài chính</CardTitle>
                                      </CardHeader>
                                      <CardContent className="text-sm space-y-2">
                                        <div><strong>Tổng COD:</strong> {formatCurrency(record.totalCOD)}</div>
                                        <div><strong>Phí dịch vụ:</strong> {formatCurrency(record.serviceFee)}</div>
                                        <div><strong>Thực nhận:</strong> <span className="text-green-600 font-medium">{formatCurrency(record.netAmount)}</span></div>
                                      </CardContent>
                                    </Card>
                                  </div>
                                  <Card>
                                    <CardHeader className="pb-3">
                                      <CardTitle className="text-base">Danh sách đơn hàng</CardTitle>
                                    </CardHeader>
                                    <CardContent>
                                      <Table>
                                        <TableHeader>
                                          <TableRow>
                                            <TableHead>Mã đơn</TableHead>
                                            <TableHead>COD</TableHead>
                                            <TableHead>Phí</TableHead>
                                          </TableRow>
                                        </TableHeader>
                                        <TableBody>
                                          {record.orders.map((order) => (
                                            <TableRow key={order.id}>
                                              <TableCell>{order.id}</TableCell>
                                              <TableCell>{formatCurrency(order.cod)}</TableCell>
                                              <TableCell>{formatCurrency(order.fee)}</TableCell>
                                            </TableRow>
                                          ))}
                                        </TableBody>
                                      </Table>
                                    </CardContent>
                                  </Card>
                                  {record.disputeReason && (
                                    <Card className="border-red-200">
                                      <CardHeader className="pb-3">
                                        <CardTitle className="text-base text-red-600">Lý do tranh chấp</CardTitle>
                                      </CardHeader>
                                      <CardContent>
                                        <p className="text-sm">{record.disputeReason}</p>
                                      </CardContent>
                                    </Card>
                                  )}
                                </div>
                              </DialogContent>
                            </Dialog>
                            {record.status === 'pending' && (
                              <Button size="sm" variant="ghost" onClick={() => handleMarkAsPaid(record.id)}>
                                <CheckCircle className="h-4 w-4" />
                              </Button>
                            )}
                            <Button size="sm" variant="ghost" onClick={() => handleExportRecord(record.id)}>
                              <Download className="h-4 w-4" />
                            </Button>
                            {record.status === 'paid' && (
                              <Button size="sm" variant="ghost" onClick={() => handleCreateDispute(record.id)}>
                                <AlertCircle className="h-4 w-4" />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="cod" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Thu COD thời gian thực</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Mã thu COD</TableHead>
                      <TableHead>Đơn hàng</TableHead>
                      <TableHead>Khách hàng</TableHead>
                      <TableHead>Số tiền</TableHead>
                      <TableHead>Tài xế</TableHead>
                      <TableHead>Thời gian thu</TableHead>
                      <TableHead>Đối soát</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {codRecords.map((record) => (
                      <TableRow key={record.id}>
                        <TableCell className="font-medium">{record.id}</TableCell>
                        <TableCell>{record.orderId}</TableCell>
                        <TableCell>{record.customerName}</TableCell>
                        <TableCell className="font-medium text-green-600">
                          {formatCurrency(record.amount)}
                        </TableCell>
                        <TableCell>{record.driver}</TableCell>
                        <TableCell>
                          {new Date(record.collectedAt).toLocaleString('vi-VN')}
                        </TableCell>
                        <TableCell>
                          {record.reconciled ? (
                            <Badge className="bg-green-100 text-green-800">
                              <CheckCircle className="h-3 w-3 mr-1" />
                              Đã đối soát
                            </Badge>
                          ) : (
                            <Badge className="bg-yellow-100 text-yellow-800">
                              <Clock className="h-3 w-3 mr-1" />
                              Chưa đối soát
                            </Badge>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="reports" className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <Card className="cursor-pointer hover:shadow-lg transition-shadow">
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <FileSpreadsheet className="h-4 w-4" />
                  Báo cáo đối soát tuần
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground mb-3">
                  Báo cáo tổng hợp đối soát theo tuần
                </p>
                <Button size="sm" className="w-full">
                  <Download className="h-4 w-4 mr-2" />
                  Tải xuống
                </Button>
              </CardContent>
            </Card>

            <Card className="cursor-pointer hover:shadow-lg transition-shadow">
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <Receipt className="h-4 w-4" />
                  Báo cáo thu COD
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground mb-3">
                  Báo cáo chi tiết thu COD theo thời gian
                </p>
                <Button size="sm" className="w-full">
                  <Download className="h-4 w-4 mr-2" />
                  Tải xuống
                </Button>
              </CardContent>
            </Card>

            <Card className="cursor-pointer hover:shadow-lg transition-shadow">
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <Users className="h-4 w-4" />
                  Báo cáo theo khách hàng
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground mb-3">
                  Báo cáo đối soát phân tích theo khách hàng
                </p>
                <Button size="sm" className="w-full">
                  <Download className="h-4 w-4 mr-2" />
                  Tải xuống
                </Button>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default Reconciliation;
